import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountType, setAccountType] = useState("Indépendant");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ name, email, password, plan: "Gratuit", account_type: accountType });
      toast.success("Compte créé");
      navigate("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputClass = "h-12 rounded-none border-0 border-b border-[var(--border)] focus-visible:border-emerald-500 focus-visible:ring-0 px-0 text-base bg-transparent text-[var(--text)]";
  const labelClass = "block hp-label mb-2";

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] antialiased grid lg:grid-cols-2">
      <div className="flex flex-col min-h-screen">
        <div className="px-6 py-4 border-b border-[var(--border)]">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <div className="w-8 h-8 rounded-lg bg-emerald-500 grid place-items-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={1.8} />
            </div>
            <span className="font-display font-semibold text-xl tracking-tight">Prosperr</span>
          </Link>
        </div>

        <div className="flex-1 grid place-items-center px-6 py-12 relative">
          <div className="brand-glow absolute inset-0 pointer-events-none opacity-50" />
          <div className="w-full max-w-md relative">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.05]">
              Créez votre compte Prosperr.
            </h1>
            <p className="mt-4 text-base text-zinc-400">
              Démarrez votre essai gratuit de 14 jours. Sans carte bancaire ni engagement.
            </p>

            <form onSubmit={submit} className="mt-10 space-y-5">
              <div>
                <label htmlFor="name" className={labelClass}>Nom complet</label>
                <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" className={inputClass} />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>Email professionnel</label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" className={inputClass} />
              </div>
              <div>
                <label htmlFor="password" className={labelClass}>Mot de passe</label>
                <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Type de compte</label>
                <Select value={accountType} onValueChange={setAccountType}>
                  <SelectTrigger className="h-12 rounded-none border-0 border-b border-[var(--border)] focus:ring-0 focus:border-emerald-500 px-0 text-base bg-transparent text-[var(--text)] [&>svg]:text-zinc-400" data-testid="register-account-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]">
                    <SelectItem value="Indépendant">Indépendant</SelectItem>
                    <SelectItem value="Entreprise">Entreprise</SelectItem>
                    <SelectItem value="White Label">White Label</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button type="submit" disabled={loading} data-testid="register-submit" className="hp-btn hp-btn-primary w-full h-14 text-base mt-6">
                {loading ? "Création…" : "Créer mon compte"}
                <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-[11px] text-zinc-500 leading-relaxed text-center mt-4" data-testid="register-rgpd-notice">
                En créant un compte, vous acceptez nos{" "}
                <Link to="/privacy" className="text-zinc-300 underline underline-offset-2 hover:text-white">conditions d'utilisation</Link>
                {" "}et notre{" "}
                <Link to="/privacy" className="text-zinc-300 underline underline-offset-2 hover:text-white">politique de confidentialité RGPD</Link>.
                Vos données sont hébergées exclusivement en UE. Droit à l'oubli en 1 clic.
              </p>
            </form>

            <div className="my-8 flex items-center gap-4 hp-label">
              <div className="h-px bg-[var(--border)] flex-1" />ou<div className="h-px bg-[var(--border)] flex-1" />
            </div>

            <button onClick={googleLogin} data-testid="google-register" className="hp-btn hp-btn-secondary w-full h-14 text-base">
              Continuer avec Google
            </button>

            <p className="text-sm text-zinc-400 text-center mt-10">
              Déjà un compte ?{" "}
              <Link to="/login" className="text-emerald-400 font-semibold underline underline-offset-4 hover:text-emerald-300">
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative bg-black overflow-hidden border-l border-[var(--border)]">
        <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1800&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-[var(--bg)]/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="hp-label mb-3" style={{ color: "#86efac" }}>500+ PME françaises</div>
          <div className="font-display text-2xl lg:text-3xl font-semibold leading-tight max-w-md">
            Rejoignez ceux qui prospectent mieux, pas plus.
          </div>
        </div>
      </div>
    </div>
  );
}
